import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, FileText, Calendar, Clock, Heart, Plus, Trash2, Loader2, 
  Printer, Sparkles, BookOpen, AlertTriangle, Download, Volume2, 
  VolumeX, Mic, Phone, PhoneOff, Star
} from 'lucide-react';
import { toast } from 'sonner';

const StudyHub = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('worksheet');
  const tabs = [
    { id: 'worksheet', label: 'Worksheets', icon: <FileText className="w-4 h-4" />, color: 'from-blue-500 to-indigo-500' },
    { id: 'timetable', label: 'My Timetable', icon: <Clock className="w-4 h-4" />, color: 'from-green-500 to-emerald-500' },
    { id: 'tests', label: 'Test Alerts', icon: <Calendar className="w-4 h-4" />, color: 'from-amber-500 to-orange-500' },
    { id: 'emotional', label: 'How I Feel', icon: <Heart className="w-4 h-4" />, color: 'from-pink-500 to-rose-500' },
    { id: 'voice', label: 'Voice Tutor', icon: <Phone className="w-4 h-4" />, color: 'from-purple-500 to-violet-500' },
    { id: 'stickers', label: 'My Stickers', icon: <Star className="w-4 h-4" />, color: 'from-yellow-500 to-amber-500' },
  ];

  return (
    <div className="min-h-screen bg-[#F4F8FB]" data-testid="study-hub-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#1E293B] via-[#334155] to-[#1E293B] relative overflow-hidden">
        <div className="absolute inset-0"><div className="absolute top-0 right-0 w-96 h-96 bg-[#4D96FF]/10 rounded-full blur-3xl -translate-y-1/2"></div></div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-white/70 hover:text-white font-semibold mb-3"><ArrowLeft className="w-5 h-5" />Dashboard</button>
          <h1 className="text-3xl sm:text-4xl font-black text-white">Study Hub</h1>
          <p className="text-white/60 text-sm sm:text-base">Your personal study command center</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 -mt-5">
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`flex items-center gap-2 px-4 sm:px-5 py-3 rounded-2xl font-bold text-xs sm:text-sm whitespace-nowrap transition-all shadow-md ${
                tab === t.id ? `bg-gradient-to-r ${t.color} text-white shadow-lg scale-105` : 'bg-white text-[#64748B] hover:bg-slate-50'
              }`}>
              {t.icon}<span className="hidden sm:inline">{t.label}</span><span className="sm:hidden">{t.label.split(' ')[0]}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <AnimatePresence mode="wait">
          <motion.div key={tab} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3 }}>
            {tab === 'worksheet' && <WorksheetTab />}
            {tab === 'timetable' && <TimetableTab />}
            {tab === 'tests' && <TestsTab />}
            {tab === 'emotional' && <EmotionalTab />}
            {tab === 'voice' && <VoiceTutorTab />}
            {tab === 'stickers' && <StickersTab />}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

// ========== WORKSHEET TAB ==========
const WorksheetTab = () => {
  const { user } = useAuth();
  const [subject, setSubject] = useState('mathematics');
  const [worksheet, setWorksheet] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try { const res = await apiService.generateWorksheet({ subject, num_questions: 15 }); setWorksheet(res.data); toast.success('Worksheet generated!'); }
    catch (e) { toast.error('Failed to generate'); } finally { setLoading(false); }
  };

  const handlePrint = () => {
    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>SkillRise Worksheet</title><style>
      @import url('https://fonts.googleapis.com/css2?family=Quicksand:wght@400;600;700&display=swap');
      *{margin:0;padding:0;box-sizing:border-box}body{font-family:'Quicksand',sans-serif;max-width:780px;margin:0 auto;padding:30px;color:#1E293B;line-height:1.6}
      .header{text-align:center;border-bottom:3px solid #4D96FF;padding-bottom:15px;margin-bottom:20px}
      .header h1{font-size:24px;color:#4D96FF;letter-spacing:1px}.header .sub{color:#94A3B8;font-size:11px;text-transform:uppercase;letter-spacing:2px}
      .info{display:flex;justify-content:space-between;font-size:13px;margin:8px 0;color:#64748B}.info b{color:#1E293B}
      .divider{border:none;border-top:2px dashed #E2E8F0;margin:15px 0}
      .content{font-size:14px;line-height:2.2;white-space:pre-wrap}
      .content strong{color:#4D96FF}.footer{text-align:center;margin-top:30px;padding-top:15px;border-top:2px solid #E2E8F0;color:#94A3B8;font-size:10px}
      @media print{body{padding:15px}}
    </style></head><body>
      <div class="header"><h1>SKILLRISE WORKSHEET</h1><div class="sub">by Ekamraj Productions</div></div>
      <div class="info"><span><b>Student:</b> ${user?.username}</span><span><b>Grade:</b> ${user?.grade_level}</span><span><b>Subject:</b> ${subject}</span><span><b>Date:</b> ${new Date().toLocaleDateString('en-IN')}</span></div>
      <div class="info"><span><b>Focus:</b> ${worksheet?.focus_topics?.join(', ')}</span><span><b>Total Questions:</b> 15</span></div>
      <hr class="divider"><div class="content">${worksheet?.content?.replace(/\n/g, '<br>')}</div>
      <div class="footer">Generated by SkillRise AI | Ekamraj Productions | ${new Date().toLocaleDateString('en-IN')}</div></body></html>`);
    w.document.close(); w.print();
  };

  const subjects = [
    { id: 'mathematics', label: 'Math', icon: '🧮', gradient: 'from-blue-500 to-indigo-600' },
    { id: 'science', label: 'Science', icon: '🔬', gradient: 'from-green-500 to-emerald-600' },
    { id: 'english', label: 'English', icon: '📖', gradient: 'from-purple-500 to-violet-600' },
  ];

  return (
    <div>
      <div className="bg-white rounded-3xl p-6 sm:p-8 border-2 border-slate-100 shadow-xl mb-6">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center flex-shrink-0">
            <FileText className="w-7 h-7 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-black text-[#1E293B]">AI Worksheet Generator</h2>
            <p className="text-[#64748B] text-sm">Creates personalized worksheets targeting YOUR weak areas. Math = pure calculations!</p>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 mb-5">
          {subjects.map(s => (
            <button key={s.id} onClick={() => setSubject(s.id)} data-testid={`ws-subject-${s.id}`}
              className={`p-4 rounded-2xl text-center transition-all border-2 ${subject === s.id ? `bg-gradient-to-br ${s.gradient} text-white border-transparent shadow-lg scale-105` : 'bg-slate-50 border-slate-200 text-[#64748B] hover:border-blue-300'}`}>
              <span className="text-2xl block mb-1">{s.icon}</span>
              <span className="font-bold text-sm">{s.label}</span>
            </button>
          ))}
        </div>
        <motion.button onClick={generate} disabled={loading} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} data-testid="generate-worksheet-btn"
          className="w-full py-4 bg-gradient-to-r from-[#4D96FF] to-[#A493FF] text-white rounded-2xl font-bold text-lg border-b-4 border-black/20 active:border-b-0 active:translate-y-1 transition-all disabled:opacity-50 flex items-center justify-center gap-2">
          {loading ? <><Loader2 className="w-5 h-5 animate-spin" />Generating your worksheet...</> : <><Sparkles className="w-5 h-5" />Generate Worksheet</>}
        </motion.button>
      </div>

      <AnimatePresence>
        {worksheet && (
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-3xl border-2 border-slate-100 shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-[#4D96FF] to-[#A493FF] p-5 flex items-center justify-between">
              <div className="text-white"><h3 className="text-xl font-bold">Your Worksheet</h3><p className="text-white/70 text-sm">Focus: {worksheet.focus_topics?.join(', ')}</p></div>
              <button onClick={handlePrint} data-testid="print-worksheet-btn"
                className="px-5 py-2.5 bg-white text-[#4D96FF] rounded-xl font-bold flex items-center gap-2 hover:scale-105 transition-all shadow-lg">
                <Printer className="w-4 h-4" />Print
              </button>
            </div>
            <div className="p-6 sm:p-8">
              <div className="prose max-w-none whitespace-pre-wrap leading-relaxed text-[#1E293B] bg-[#F8FAFC] rounded-2xl p-5 sm:p-6 border border-slate-200 text-sm sm:text-base"
                dangerouslySetInnerHTML={{ __html: formatWorksheet(worksheet.content) }} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const formatWorksheet = (content) => {
  if (!content) return '';
  return content.replace(/\n/g, '<br/>').replace(/Q(\d+)\./g, '<strong style="color:#4D96FF;font-size:1.1em">Q$1.</strong>')
    .replace(/Answer:/g, '<em style="color:#38E54D;font-weight:700">Answer:</em>')
    .replace(/Section ([A-D])/g, '<div style="background:#EEF2FF;padding:8px 16px;border-radius:12px;font-weight:800;color:#4D96FF;margin:16px 0 8px;display:inline-block">Section $1</div><br/>')
    .replace(/\(Marks: (\d+)\)/g, '<span style="background:#FFF3D6;color:#E6A820;padding:2px 8px;border-radius:8px;font-size:0.8em;font-weight:700">$1 mark(s)</span>');
};

// ========== TIMETABLE TAB ==========
const TimetableTab = () => {
  const [timetable, setTimetable] = useState(null);
  const [hours, setHours] = useState(3);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    setLoading(true);
    try { const res = await apiService.generateTimetable({ preferred_study_hours: hours, include_breaks: true }); setTimetable(res.data.timetable); toast.success('Timetable ready!'); }
    catch (e) { toast.error('Failed'); } finally { setLoading(false); }
  };

  return (
    <div>
      <div className="bg-white rounded-3xl p-6 sm:p-8 border-2 border-slate-100 shadow-xl mb-6">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center flex-shrink-0"><Clock className="w-7 h-7 text-white" /></div>
          <div><h2 className="text-2xl font-black text-[#1E293B]">My Study Timetable</h2><p className="text-[#64748B] text-sm">A relaxed plan with breaks & fun time. Zero pressure!</p></div>
        </div>
        <p className="font-bold text-[#1E293B] mb-3">How many hours can you study per day?</p>
        <div className="flex gap-2 mb-5">
          {[1,2,3,4,5].map(h => (
            <motion.button key={h} onClick={() => setHours(h)} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
              className={`w-12 h-12 rounded-2xl font-black text-lg transition-all ${hours === h ? 'bg-gradient-to-br from-green-500 to-emerald-600 text-white shadow-lg' : 'bg-slate-100 text-[#64748B]'}`}>{h}h</motion.button>
          ))}
        </div>
        <motion.button onClick={generate} disabled={loading} whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} data-testid="generate-timetable-btn"
          className="w-full py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-2xl font-bold text-lg border-b-4 border-black/20 transition-all disabled:opacity-50 flex items-center justify-center gap-2">
          {loading ? <><Loader2 className="w-5 h-5 animate-spin" />Creating...</> : 'Create My Timetable'}
        </motion.button>
      </div>
      {timetable && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-3xl p-6 sm:p-8 border-2 border-slate-100 shadow-xl">
          <div className="whitespace-pre-wrap leading-relaxed text-[#1E293B] text-sm sm:text-base"
            dangerouslySetInnerHTML={{ __html: timetable.replace(/\n/g, '<br/>').replace(/(MONDAY|TUESDAY|WEDNESDAY|THURSDAY|FRIDAY|SATURDAY|SUNDAY)/g, '<div style="background:linear-gradient(135deg,#4D96FF,#A493FF);color:white;display:inline-block;padding:6px 16px;border-radius:12px;font-weight:800;margin:12px 0 6px">$1</div>') }} />
        </motion.div>
      )}
    </div>
  );
};

// ========== TESTS TAB ==========
const TestsTab = () => {
  const [tests, setTests] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ subject: 'mathematics', test_name: '', test_date: '', chapters: '', notes: '' });

  useEffect(() => { fetchTests(); }, []);
  const fetchTests = async () => { try { const r = await apiService.getUpcomingTests(); setTests(r.data.tests); } catch (e) {} };

  const addTest = async () => {
    if (!form.test_name || !form.test_date) { toast.error('Fill test name and date'); return; }
    try {
      await apiService.addUpcomingTest({ ...form, chapters: form.chapters.split(',').map(c => c.trim()).filter(Boolean) });
      toast.success('Test added! AI tutor will help you prepare.');
      setShowForm(false); setForm({ subject: 'mathematics', test_name: '', test_date: '', chapters: '', notes: '' }); fetchTests();
    } catch (e) { toast.error('Failed'); }
  };

  const deleteTest = async (id) => { await apiService.deleteUpcomingTest(id); fetchTests(); toast.success('Removed'); };
  const daysUntil = (d) => { const diff = Math.ceil((new Date(d) - new Date()) / 86400000); return diff; };

  return (
    <div>
      <div className="bg-white rounded-3xl p-6 sm:p-8 border-2 border-slate-100 shadow-xl">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-600 rounded-2xl flex items-center justify-center flex-shrink-0"><Calendar className="w-7 h-7 text-white" /></div>
            <div><h2 className="text-2xl font-black text-[#1E293B]">Upcoming Tests</h2><p className="text-[#64748B] text-sm">Add tests & your AI tutor will prepare you!</p></div>
          </div>
          <motion.button onClick={() => setShowForm(!showForm)} whileHover={{ scale: 1.05 }} data-testid="add-test-btn"
            className="px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-bold flex items-center gap-1 shadow-lg text-sm">
            <Plus className="w-4 h-4" />Add
          </motion.button>
        </div>

        <AnimatePresence>
          {showForm && (
            <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
              className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-5 mb-6 border-2 border-amber-200 overflow-hidden">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input type="text" placeholder="Test name (e.g. Unit Test 2)" value={form.test_name} onChange={e => setForm({...form, test_name: e.target.value})}
                  className="p-3 bg-white rounded-xl border-2 border-slate-200 font-medium text-sm" data-testid="test-name-input" />
                <input type="date" value={form.test_date} onChange={e => setForm({...form, test_date: e.target.value})}
                  className="p-3 bg-white rounded-xl border-2 border-slate-200 font-medium text-sm" data-testid="test-date-input" />
                <select value={form.subject} onChange={e => setForm({...form, subject: e.target.value})} className="p-3 bg-white rounded-xl border-2 border-slate-200 font-medium text-sm">
                  <option value="mathematics">Mathematics</option><option value="science">Science</option><option value="english">English</option>
                </select>
                <input type="text" placeholder="Chapters (comma separated)" value={form.chapters} onChange={e => setForm({...form, chapters: e.target.value})}
                  className="p-3 bg-white rounded-xl border-2 border-slate-200 font-medium text-sm" />
              </div>
              <button onClick={addTest} className="mt-3 px-6 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-xl font-bold text-sm" data-testid="save-test-btn">Save Test</button>
            </motion.div>
          )}
        </AnimatePresence>

        {tests.length === 0 ? (
          <div className="text-center py-10"><span className="text-5xl block mb-3">📅</span><p className="text-[#64748B] font-semibold">No upcoming tests added yet</p><p className="text-sm text-[#94A3B8]">Add a test and your AI tutor will help you prepare!</p></div>
        ) : (
          <div className="space-y-3">
            {tests.map(t => {
              const days = daysUntil(t.test_date);
              const urgent = days >= 0 && days <= 3;
              const today = days === 0;
              return (
                <motion.div key={t.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                  className={`p-4 rounded-2xl border-2 flex items-center gap-4 ${today ? 'bg-red-50 border-red-400 ring-2 ring-red-200' : urgent ? 'bg-orange-50 border-orange-300' : 'bg-slate-50 border-slate-200'}`}>
                  <div className={`w-14 h-14 rounded-2xl flex flex-col items-center justify-center flex-shrink-0 ${today ? 'bg-red-500 text-white' : urgent ? 'bg-orange-500 text-white' : 'bg-slate-200 text-[#64748B]'}`}>
                    <span className="text-lg font-black leading-none">{days >= 0 ? days : '!'}</span>
                    <span className="text-[9px] font-bold">{today ? 'TODAY' : days > 0 ? 'days' : 'past'}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-[#1E293B] truncate">{t.test_name}</h3>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-600 rounded-lg font-bold capitalize">{t.subject}</span>
                      <span className="text-xs text-[#64748B]">{t.test_date}</span>
                    </div>
                  </div>
                  <button onClick={() => deleteTest(t.id)} className="text-slate-400 hover:text-red-500 flex-shrink-0"><Trash2 className="w-4 h-4" /></button>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ========== EMOTIONAL TAB ==========
const EmotionalTab = () => {
  const [response, setResponse] = useState(null);
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState('');

  const checkin = async (mood) => {
    setSelected(mood);
    try { const res = await apiService.emotionalCheckin({ mood, energy_level: 3, notes }); setResponse(res.data); toast.success('Thanks for sharing!'); }
    catch (e) { toast.error('Failed'); }
  };

  const moods = [
    { id: 'happy', emoji: '😊', label: 'Happy', bg: 'from-green-400 to-emerald-500' },
    { id: 'okay', emoji: '😌', label: 'Okay', bg: 'from-sky-400 to-blue-500' },
    { id: 'stressed', emoji: '😰', label: 'Stressed', bg: 'from-orange-400 to-red-500' },
    { id: 'confused', emoji: '🤔', label: 'Confused', bg: 'from-violet-400 to-purple-500' },
    { id: 'sad', emoji: '😢', label: 'Sad', bg: 'from-slate-400 to-slate-600' },
  ];

  return (
    <div>
      <div className="bg-white rounded-3xl p-6 sm:p-8 border-2 border-slate-100 shadow-xl">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-14 h-14 bg-gradient-to-br from-pink-500 to-rose-600 rounded-2xl flex items-center justify-center flex-shrink-0"><Heart className="w-7 h-7 text-white" /></div>
          <div><h2 className="text-2xl font-black text-[#1E293B]">How Are You Feeling?</h2><p className="text-[#64748B] text-sm">Your feelings matter! We'll adjust your learning based on how you feel.</p></div>
        </div>
        
        <div className="grid grid-cols-5 gap-2 sm:gap-3 mb-6">
          {moods.map(m => (
            <motion.button key={m.id} onClick={() => checkin(m.id)} whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }} data-testid={`mood-${m.id}`}
              className={`p-3 sm:p-5 rounded-2xl text-center transition-all ${selected === m.id ? `bg-gradient-to-br ${m.bg} text-white shadow-xl scale-105 ring-4 ring-white` : 'bg-slate-50 border-2 border-slate-200 hover:border-slate-300'}`}>
              <span className="text-3xl sm:text-5xl block mb-1">{m.emoji}</span>
              <span className="text-[10px] sm:text-xs font-bold">{m.label}</span>
            </motion.button>
          ))}
        </div>

        <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
          className="w-full p-3 bg-[#F4F8FB] border-2 border-slate-200 rounded-2xl text-sm mb-4" placeholder="Want to share more? (optional)" />

        <AnimatePresence>
          {response && (
            <motion.div initial={{ opacity: 0, y: 20, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }}
              className="bg-gradient-to-r from-[#FFF3D6] to-[#FFE8D6] rounded-2xl p-6 border-2 border-[#FFC03D]/30">
              <p className="text-lg font-bold text-[#1E293B] mb-2">{response.message}</p>
              <div className="bg-white/60 rounded-xl p-3"><p className="text-sm font-semibold text-[#64748B]">Suggestion: {response.suggestion}</p></div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

// ========== VOICE TUTOR TAB (Whisper STT + Browser TTS - WhatsApp-style call) ==========
const VoiceTutorTab = () => {
  const { user } = useAuth();
  const tutorName = user?.tutor_name || 'Buddy';
  const [callActive, setCallActive] = useState(false);
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [response, setResponse] = useState('');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [conversation, setConversation] = useState([]); // {role, text}
  const [sessionId] = useState(`voice_${Date.now()}`);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const synthRef = useRef(window.speechSynthesis);
  const audioCtxRef = useRef(null);
  const silenceTimeoutRef = useRef(null);

  const speak = (text) => {
    if (synthRef.current.speaking) synthRef.current.cancel();
    const utterance = new SpeechSynthesisUtterance(text.replace(/\*\*/g, '').replace(/[#*_`]/g, ''));
    utterance.rate = 0.95; utterance.pitch = 1.1;
    const voices = synthRef.current.getVoices();
    const v = voices.find(v => v.lang.startsWith('en-IN')) ||
              voices.find(v => v.name.toLowerCase().includes('google') && v.lang.startsWith('en')) ||
              voices.find(v => v.lang.startsWith('en'));
    if (v) utterance.voice = v;
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    synthRef.current.speak(utterance);
  };

  const stopSpeaking = () => { synthRef.current.cancel(); setIsSpeaking(false); };

  const startCall = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Just check permission then stop tracks (we'll start fresh on each recording)
      stream.getTracks().forEach(t => t.stop());
      setCallActive(true);
      setConversation([]);
      // Greeting
      const greeting = `Hi ${user?.username || 'friend'}! I'm ${tutorName}. Tap the mic and ask me anything you'd like to learn today.`;
      setResponse(greeting);
      setConversation([{ role: 'tutor', text: greeting }]);
      setTimeout(() => speak(greeting), 300);
    } catch (e) {
      toast.error('Microphone permission needed for voice call');
    }
  };

  const endCall = () => {
    stopSpeaking();
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setCallActive(false);
    setRecording(false);
    setTranscript('');
    setResponse('');
  };

  const startRecording = async () => {
    try {
      stopSpeaking();
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        if (audioCtxRef.current) { try { audioCtxRef.current.close(); } catch (e) {} audioCtxRef.current = null; }
        if (silenceTimeoutRef.current) { clearTimeout(silenceTimeoutRef.current); silenceTimeoutRef.current = null; }
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        if (blob.size < 1500) {
          toast.error('Recording too short - please speak longer');
          return;
        }
        await processAudio(blob);
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);

      // Voice Activity Detection: auto-stop after 1.5s of silence
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        audioCtxRef.current = ctx;
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        source.connect(analyser);
        const buffer = new Uint8Array(analyser.frequencyBinCount);
        let lastSpeechAt = Date.now();
        const tick = () => {
          if (!audioCtxRef.current) return;
          analyser.getByteFrequencyData(buffer);
          const sum = buffer.reduce((a, b) => a + b, 0);
          const avg = sum / buffer.length;
          if (avg > 8) lastSpeechAt = Date.now();
          if (Date.now() - lastSpeechAt > 1500 && mr.state === 'recording') {
            mr.stop(); setRecording(false); return;
          }
          requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      } catch (e) { /* VAD optional */ }
    } catch (e) {
      toast.error('Could not access microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
      setRecording(false);
    }
  };

  const processAudio = async (blob) => {
    setProcessing(true);
    try {
      // 1. Transcribe with Whisper
      const fd = new FormData();
      fd.append('audio', blob, 'recording.webm');
      const trRes = await apiService.transcribeAudio(fd);
      const text = trRes.data.transcript;
      if (!text || text.trim().length < 2) {
        toast.error("Couldn't hear you clearly - try again");
        setProcessing(false);
        return;
      }
      setTranscript(text);
      setConversation(prev => [...prev, { role: 'user', text }]);

      // 2. Send to voice chat AI
      const chatRes = await apiService.voiceChat({ transcript: text, session_id: sessionId });
      const reply = chatRes.data.response;
      setResponse(reply);
      setConversation(prev => [...prev, { role: 'tutor', text: reply }]);
      speak(reply);
    } catch (e) {
      toast.error('Voice tutor unavailable - try again');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div>
      <div className="bg-white rounded-3xl p-6 sm:p-8 border-2 border-slate-100 shadow-xl">
        <div className="flex items-start gap-4 mb-6">
          <motion.div animate={isSpeaking ? { scale: [1, 1.1, 1] } : {}} transition={{ repeat: Infinity, duration: 1 }}
            className="w-14 h-14 bg-gradient-to-br from-purple-500 to-violet-600 rounded-2xl flex items-center justify-center flex-shrink-0">
            <Phone className="w-7 h-7 text-white" />
          </motion.div>
          <div>
            <h2 className="text-2xl font-black text-[#1E293B]">Voice Call with {tutorName}</h2>
            <p className="text-[#64748B] text-sm">Real voice conversation - just tap the mic and speak!</p>
          </div>
        </div>

        {!callActive ? (
          // Call start screen
          <div className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-3xl p-8 sm:p-12 text-center border-2 border-purple-200">
            <motion.div animate={{ scale: [1, 1.05, 1] }} transition={{ repeat: Infinity, duration: 2 }}
              className="w-32 h-32 mx-auto mb-6 bg-gradient-to-br from-purple-500 to-violet-600 rounded-full flex items-center justify-center shadow-2xl">
              <Phone className="w-16 h-16 text-white" />
            </motion.div>
            <h3 className="text-2xl font-black text-[#1E293B] mb-2">Ready to chat with {tutorName}?</h3>
            <p className="text-[#64748B] mb-6 text-sm max-w-md mx-auto">
              Have a real voice conversation. Ask any doubt, request explanations, or just chat about your studies!
            </p>
            <motion.button onClick={startCall} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} data-testid="start-call-btn"
              className="px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-2xl font-black text-lg shadow-xl flex items-center gap-2 mx-auto">
              <Phone className="w-5 h-5" />Start Call
            </motion.button>
          </div>
        ) : (
          // Active call screen
          <div className="bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 rounded-3xl p-6 sm:p-8 border-2 border-purple-500/30">
            {/* Avatar + status */}
            <div className="text-center mb-6">
              <motion.div
                animate={isSpeaking ? { scale: [1, 1.08, 1] } : recording ? { scale: [1, 1.05, 1] } : {}}
                transition={{ repeat: Infinity, duration: 0.8 }}
                className="w-28 h-28 sm:w-32 sm:h-32 mx-auto rounded-full bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center text-5xl font-black text-white shadow-2xl ring-4 ring-white/20"
                style={{ backgroundColor: user?.avatar_skin || '#FFD5A0' }}>
                {tutorName[0]}
              </motion.div>
              <h3 className="text-xl font-bold text-white mt-3">{tutorName}</h3>
              <p className="text-purple-200 text-sm">
                {processing ? 'Thinking...' : isSpeaking ? `${tutorName} is speaking...` : recording ? 'Listening...' : 'Tap mic to speak'}
              </p>
              {/* Audio bars when speaking */}
              {(isSpeaking || recording) && (
                <div className="flex justify-center gap-1 mt-3 h-6">
                  {[0, 1, 2, 3, 4].map(i => (
                    <motion.div key={i} className="w-1.5 bg-white rounded-full"
                      animate={{ height: ['20%', '100%', '40%', '80%', '30%'] }}
                      transition={{ repeat: Infinity, duration: 0.6, delay: i * 0.1 }} />
                  ))}
                </div>
              )}
            </div>

            {/* Conversation transcript */}
            <div className="bg-black/30 rounded-2xl p-4 max-h-64 overflow-y-auto mb-5 scrollbar-thin">
              {conversation.length === 0 ? (
                <p className="text-purple-200/60 text-sm text-center py-4">Say "Hi" to start the conversation...</p>
              ) : (
                <div className="space-y-3">
                  {conversation.map((m, i) => (
                    <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm ${
                        m.role === 'user' ? 'bg-purple-500 text-white' : 'bg-white/10 text-white'
                      }`}>
                        {m.text}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Call controls */}
            <div className="flex justify-center items-center gap-4">
              {/* Mic button (push to talk) */}
              <motion.button
                onMouseDown={startRecording} onMouseUp={stopRecording}
                onTouchStart={startRecording} onTouchEnd={stopRecording}
                disabled={processing || isSpeaking}
                whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                data-testid="mic-record-btn"
                className={`w-20 h-20 rounded-full flex items-center justify-center shadow-2xl transition-all ${
                  recording ? 'bg-red-500 ring-4 ring-red-300 animate-pulse' : 'bg-white text-purple-600'
                } ${(processing || isSpeaking) ? 'opacity-40 cursor-not-allowed' : ''}`}>
                <Mic className={`w-9 h-9 ${recording ? 'text-white' : 'text-purple-600'}`} />
              </motion.button>

              {/* Stop speaking */}
              {isSpeaking && (
                <motion.button onClick={stopSpeaking} whileTap={{ scale: 0.9 }}
                  className="w-14 h-14 bg-amber-500 rounded-full flex items-center justify-center shadow-xl">
                  <VolumeX className="w-6 h-6 text-white" />
                </motion.button>
              )}

              {/* End call */}
              <motion.button onClick={endCall} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} data-testid="end-call-btn"
                className="w-14 h-14 bg-red-600 rounded-full flex items-center justify-center shadow-xl">
                <PhoneOff className="w-6 h-6 text-white" />
              </motion.button>
            </div>
            <p className="text-center text-purple-200/70 text-xs mt-4">
              Hold the mic button and speak, then release. {tutorName} will reply with voice!
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// ========== STICKERS TAB ==========
const StickersTab = () => {
  const [stickers, setStickers] = useState([]);
  const [newOnes, setNewOnes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try { const res = await apiService.getStickers(); setStickers(res.data.stickers); setNewOnes(res.data.new_stickers || []); }
      catch (e) {} finally { setLoading(false); }
    }; fetch();
  }, []);

  const earned = stickers.filter(s => s.earned).length;

  return (
    <div>
      <div className="bg-white rounded-3xl p-6 sm:p-8 border-2 border-slate-100 shadow-xl">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-14 h-14 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-2xl flex items-center justify-center flex-shrink-0"><Star className="w-7 h-7 text-white" /></div>
          <div><h2 className="text-2xl font-black text-[#1E293B]">My Sticker Collection</h2><p className="text-[#64748B] text-sm">Earn stickers as you learn and grow! {earned}/{stickers.length} collected</p></div>
        </div>

        {/* Progress */}
        <div className="mb-6 bg-amber-50 rounded-2xl p-4">
          <div className="flex justify-between text-sm font-bold mb-2"><span className="text-amber-700">Collection Progress</span><span className="text-amber-700">{earned}/{stickers.length}</span></div>
          <div className="bg-amber-200 rounded-full h-3"><motion.div className="bg-gradient-to-r from-yellow-400 to-amber-500 h-3 rounded-full" initial={{ width: 0 }} animate={{ width: `${(earned / Math.max(stickers.length, 1)) * 100}%` }} transition={{ duration: 1 }} /></div>
        </div>

        {loading ? <div className="text-center py-8"><Loader2 className="w-8 h-8 animate-spin mx-auto text-amber-500" /></div> : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
            {stickers.map((s, i) => (
              <motion.div key={s.id} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.05 }}
                className={`p-4 rounded-2xl border-2 text-center transition-all ${
                  s.earned ? 'bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-300 shadow-md' : 'bg-slate-50 border-slate-200 opacity-40'
                } ${newOnes.includes(s.id) ? 'ring-4 ring-amber-400 ring-offset-2 animate-bounce' : ''}`}>
                <span className="text-4xl block mb-2">{s.icon}</span>
                <p className={`font-bold text-sm ${s.earned ? 'text-[#1E293B]' : 'text-slate-400'}`}>{s.name}</p>
                <p className="text-[10px] text-[#64748B] mt-1">{s.description}</p>
                {!s.earned && <p className="text-[9px] text-slate-400 mt-1 font-bold">LOCKED</p>}
                {newOnes.includes(s.id) && <span className="text-[10px] bg-amber-500 text-white px-2 py-0.5 rounded-full font-bold mt-1 inline-block">NEW!</span>}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default StudyHub;
